/**
 * Enterprise Validation Dashboard v2 · Sprint P37q · Feb 2026
 *
 * Replaces the previous DataValidationStep with a professional QA/QC
 * panel containing:
 *   - Top summary (critical / warnings / passed / auto-fix / manual-review)
 *   - Validation cards (rule · impact · examples · buttons)
 *   - Safe auto-fix (azimuth normalisation only)
 *   - Manual review workflow (mark-unsampled / merge-adjacent)
 *   - Immutable correction history drawer
 *
 * Auto-fix rule (strict):
 *   Only mathematically deterministic corrections are exposed as
 *   Auto-Correct. Assay gaps NEVER auto-populate — they require
 *   explicit user choice.
 */
import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArrowClockwise, ArrowRight, ClockCounterClockwise, Compass, Eye,
  Info, MagicWand, MagnifyingGlass, PencilSimple, ShieldCheck, Sparkle,
  Warning, WarningCircle, X, XCircle,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const SEV_STYLE = {
  critical: { pill: "bg-rose-50 border-rose-200 text-rose-700",   icon: XCircle,       tone: "text-rose-500", label: "Critical" },
  warning:  { pill: "bg-amber-50 border-amber-200 text-amber-700", icon: WarningCircle, tone: "text-amber-500", label: "Warning" },
  info:     { pill: "bg-blue-50 border-blue-200 text-blue-700",    icon: Info,          tone: "text-blue-500",  label: "Info" },
  passed:   { pill: "bg-emerald-50 border-emerald-200 text-emerald-700", icon: ShieldCheck, tone: "text-emerald-500", label: "Passed" },
};

/* ═════════════════════════════════════════════════════════════════
 * Root component
 * ════════════════════════════════════════════════════════════════ */
export default function ValidationDashboardV2({ projectId, onDataChanged }) {
  const qc = useQueryClient();
  const [openHole, setOpenHole] = useState(null);
  const [openHistory, setOpenHistory] = useState(false);
  const [openManual, setOpenManual] = useState(null);   // { card, example }

  const valQ = useQuery({
    queryKey: ["validation-v2", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/validation/v2`).then((r) => r.data),
    enabled:  !!projectId,
  });
  const data = valQ.data;

  const refetch = useCallback(() => {
    qc.invalidateQueries({ queryKey: ["validation-v2", projectId] });
    if (onDataChanged) onDataChanged();
  }, [qc, projectId, onDataChanged]);

  const autofixAzimuth = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/validation/autofix/azimuth`).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Corrected ${r.corrected} azimuth${r.corrected === 1 ? "" : "s"} · audit ${r.audit_id?.slice(0, 8)}`);
      refetch();
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Auto-correct failed"),
  });

  // Iter98c · Rollback support — undo the most recent safe-autofix run.
  const rollbackMut = useMutation({
    mutationFn: (runId) => api.post(`/projects/${projectId}/validation/safe-autofix/rollback/${runId}`).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(
        `Rollback complete · ${r.restored} record${r.restored === 1 ? "" : "s"} restored` +
        (r.reinserted ? ` · ${r.reinserted} re-inserted.` : ".")
      );
      refetch();
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.detail
        || e?.response?.data?.message
        || "Rollback failed.";
      toast.error(String(msg));
    },
  });

  // Iter98 · Batch Safe Auto-Fix — runs deterministic, reversible
  // cleanups (case + numeric coercion + negative-depth abs + empty-row
  // removal) across every drillhole doc for the project. Every change
  // is written to `db.audit_log` with kind="safe_autofix", so the
  // Correction History drawer can be used to inspect / rollback later.
  const safeAutofix = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/validation/safe-autofix`).then((r) => r.data),
    onSuccess: (r) => {
      const applied = r?.applied ?? 0;
      const unfixed = (r?.unfixed || []).length;
      const runId = r?.run_id;
      if (applied === 0 && unfixed === 0) {
        toast.info("Nothing to fix — data is already clean.");
      } else {
        toast.success(
          `Safe auto-fix complete · ${applied} record${applied === 1 ? "" : "s"} corrected` +
          (unfixed ? ` · ${unfixed} still need manual review.` : "."),
          {
            duration: 12_000,
            action: runId ? {
              label: "Undo",
              onClick: () => rollbackMut.mutate(runId),
            } : undefined,
          },
        );
      }
      refetch();
    },
    onError: (e) => {
      const msg = e?.response?.data?.detail?.message
        || e?.response?.data?.message
        || "Safe auto-fix failed — try again or use Edit Manually.";
      toast.error(msg);
    },
  });

  if (valQ.isLoading) return <LoadingShell />;
  if (valQ.error)    return <ErrorShell error={valQ.error} />;

  const s = data?.summary || {};
  const cards = data?.cards || [];

  const handleAutoFix = (card) => {
    if (card.auto_fix?.action === "azimuth_normalize") return autofixAzimuth.mutate();
    // Iter98 · Fall back to the broad safe-autofix so users get one-
    // click reversible cleanup for every card that doesn't have a
    // dedicated per-code action.
    return safeAutofix.mutate();
  };

  return (
    <div data-testid="validation-dashboard-v2" className="space-y-4">
      {/* ── Summary bar ───────────────────────────────────────── */}
      <SummaryBar summary={s}
                     onOpenHistory={() => setOpenHistory(true)}
                     onRefresh={refetch}
                     onSafeAutofix={() => safeAutofix.mutate()}
                     safeAutofixBusy={safeAutofix.isPending}
                     refreshing={valQ.isFetching} />

      {/* ── Validation cards ─────────────────────────────────── */}
      {cards.filter((c) => c.severity !== "info").length === 0 ? (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-8 text-center"
                data-testid="validation-all-pass">
          <ShieldCheck size={26} className="mx-auto text-emerald-500" weight="fill" />
          <div className="mt-2 font-heading font-black text-lg text-emerald-800">
            All validation checks pass
          </div>
          <div className="text-[12px] text-emerald-700 mt-1">
            {s.passed} rules passed · no critical issues detected.
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {cards.filter((c) => c.severity !== "info").map((card, i) => (
            <ValidationCard
              key={`${card.code}-${i}`}
              card={card}
              onAutoFix={() => handleAutoFix(card)}
              onViewHole={(hid) => setOpenHole(hid)}
              onManual={(ex) => setOpenManual({ card, example: ex })}
              busy={
                (autofixAzimuth.isPending && card.auto_fix?.action === "azimuth_normalize")
                || (safeAutofix.isPending && card.auto_fix?.action !== "azimuth_normalize")
              }
            />
          ))}
        </div>
      )}

      {/* Info-only cards (row counts) — subtle strip at bottom */}
      {cards.filter((c) => c.severity === "info").length > 0 && (
        <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600 flex gap-4 flex-wrap">
          <Info size={12} className="text-slate-400" />
          {cards.filter((c) => c.severity === "info").map((c) => (
            <span key={c.code}>
              <span className="font-mono text-[10.5px]">{c.code}</span>
              <span className="ml-1 text-slate-800 font-semibold">{c.affected_records.toLocaleString()}</span>
            </span>
          ))}
        </div>
      )}

      {/* Modals */}
      <HistoryDrawer open={openHistory} onClose={() => setOpenHistory(false)} projectId={projectId} />
      <HoleModal open={!!openHole} onClose={() => setOpenHole(null)} projectId={projectId} holeId={openHole} />
      <ManualFixModal open={!!openManual}
                          onClose={() => setOpenManual(null)}
                          projectId={projectId}
                          card={openManual?.card}
                          example={openManual?.example}
                          onFixed={refetch} />
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════
 * Summary bar
 * ════════════════════════════════════════════════════════════════ */
function SummaryBar({ summary, onOpenHistory, onRefresh, refreshing,
                       onSafeAutofix, safeAutofixBusy }) {
  const s = summary || {};
  return (
    <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-slate-100 flex items-center gap-3 flex-wrap">
        <ShieldCheck size={14} className="text-brand-blue" weight="duotone" />
        <h3 className="font-heading font-bold text-[13.5px] tracking-tight text-slate-900 flex-1">
          Validation Summary
        </h3>
        <Button variant="ghost" size="sm" onClick={onOpenHistory} className="gap-1.5 text-[11px]"
                  data-testid="val-open-history">
          <ClockCounterClockwise size={11} /> Correction history
        </Button>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={refreshing}
                  className="gap-1.5 text-[11px]" data-testid="val-refresh">
          <ArrowClockwise size={11} className={refreshing ? "animate-spin" : ""} />
          {refreshing ? "Refreshing…" : "Refresh"}
        </Button>
        <Button size="sm" onClick={onSafeAutofix} disabled={safeAutofixBusy}
                  className="gap-1.5 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                  data-testid="val-safe-autofix">
          <MagicWand size={11} weight="fill" />
          {safeAutofixBusy ? "Running…" : "Safe Auto-Fix"}
        </Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 p-3">
        <Stat label="Critical" value={s.critical || 0} tone="critical" testId="sum-critical" />
        <Stat label="Warnings" value={s.warnings || 0} tone="warning" testId="sum-warnings" />
        <Stat label="Passed"   value={s.passed || 0}   tone="passed"  testId="sum-passed" />
        <Stat label="Auto-fix Available" value={s.auto_fix_available || 0} tone="passed" testId="sum-autofix" />
        <Stat label="Manual Review" value={s.manual_review_required || 0} tone="warning" testId="sum-manual" />
      </div>
    </div>
  );
}
function Stat({ label, value, tone, testId }) {
  const style = SEV_STYLE[tone] || SEV_STYLE.info;
  return (
    <div className={`rounded-md border px-3 py-2 ${style.pill}`} data-testid={testId}>
      <div className="text-[9.5px] uppercase tracking-widest opacity-75">{label}</div>
      <div className="mt-0.5 font-heading font-black text-[18px] leading-none">{value}</div>
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════
 * Card
 * ════════════════════════════════════════════════════════════════ */
function ValidationCard({ card, onAutoFix, onViewHole, onManual, busy }) {
  const st = SEV_STYLE[card.severity] || SEV_STYLE.info;
  const Icon = st.icon;
  return (
    <section
      data-testid={`validation-card-${card.code}`}
      className="rounded-lg border border-slate-200 bg-white overflow-hidden"
    >
      <div className="px-4 py-3 border-b border-slate-100 flex items-start gap-3">
        <Icon size={16} className={`${st.tone} mt-0.5 shrink-0`} weight="fill" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-heading font-bold text-[13.5px] tracking-tight text-slate-900">
              {card.title}
            </h4>
            <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border ${st.pill}`}>
              {st.label}
            </span>
          </div>
          <div className="mt-0.5 text-[10.5px] text-slate-500 font-mono truncate">{card.code}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] uppercase tracking-widest text-slate-500">Affected</div>
          <div className="font-heading font-black text-lg leading-none">{card.affected_records}</div>
        </div>
      </div>

      <div className="p-4 space-y-3">
        <Row label="Validation Rule"   value={card.rule} />
        {card.impact && <Row label="Potential Impact" value={card.impact} />}

        {card.examples && card.examples.length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Examples</div>
            <div className="space-y-1.5">
              {card.examples.map((ex, i) => (
                <ExampleRow key={i} card={card} ex={ex} />
              ))}
            </div>
          </div>
        )}

        {(card.affected_drillholes || []).length > 0 && (
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5">Affected Drillholes</div>
            <div className="flex flex-wrap gap-1">
              {card.affected_drillholes.slice(0, 12).map((h) => (
                <button
                  key={h}
                  onClick={() => onViewHole(h)}
                  data-testid={`val-hole-${h}`}
                  className="inline-flex items-center gap-1 h-6 px-2 rounded-sm border border-slate-300 bg-white text-slate-700 text-[10.5px] font-mono hover:bg-slate-50">
                  <MagnifyingGlass size={9} /> {h}
                </button>
              ))}
              {card.affected_drillholes.length > 12 && (
                <span className="text-[10.5px] text-slate-500 font-mono py-1">
                  +{card.affected_drillholes.length - 12} more
                </span>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 flex flex-wrap gap-1.5">
        {card.affected_drillholes?.[0] && (
          <Button size="sm" variant="outline" onClick={() => onViewHole(card.affected_drillholes[0])}
                     className="h-8 gap-1 text-[11px]" data-testid={`val-view-${card.code}`}>
            <Eye size={11} /> View Records
          </Button>
        )}
        {card.auto_fix ? (
          <Button size="sm" onClick={onAutoFix} disabled={busy}
                     className="h-8 gap-1 text-[11px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                     data-testid={`val-autofix-${card.code}`}>
            <MagicWand size={11} /> {busy ? "Correcting…" : card.auto_fix.label}
          </Button>
        ) : (
          // Iter98 · Fallback Safe Auto-Fix on every card that lacks a
          // dedicated action — non-critical severities only, to keep
          // the guard-rails: critical errors need explicit review.
          card.severity !== "critical" && (
            <Button size="sm" onClick={onAutoFix} disabled={busy} variant="outline"
                       className="h-8 gap-1 text-[11px] border-brand-blue text-brand-blue hover:bg-brand-blue/10"
                       data-testid={`val-safe-autofix-${card.code}`}>
              <MagicWand size={11} /> {busy ? "Running…" : "Safe Auto-Fix"}
            </Button>
          )
        )}
        <Button size="sm" variant="outline" onClick={() => onManual(null)}
                   className="h-8 gap-1 text-[11px]" data-testid={`val-manual-${card.code}`}>
          <PencilSimple size={11} /> Edit Manually
        </Button>
      </div>
    </section>
  );
}
function Row({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-0.5">{label}</div>
      <div className="text-[12.5px] text-slate-800 leading-snug">{value}</div>
    </div>
  );
}
function ExampleRow({ card, ex }) {
  if (card.code === "INVALID_AZIMUTH") {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 flex items-center gap-3 text-[12px]"
              data-testid={`ex-azimuth-${ex.hole_id}`}>
        <Compass size={11} className="text-brand-blue shrink-0" />
        <span className="font-mono text-slate-700 min-w-[80px]">{ex.hole_id}</span>
        <span className="text-slate-500 text-[10.5px] uppercase">Recorded</span>
        <span className="font-mono text-rose-700">{ex.recorded}</span>
        <ArrowRight size={11} className="text-slate-400" />
        <span className="text-slate-500 text-[10.5px] uppercase">Suggested</span>
        <span className="font-mono text-emerald-700 font-semibold">{ex.suggested}</span>
      </div>
    );
  }
  if (card.code === "ASSAY_GAPS") {
    return (
      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[12px]"
              data-testid={`ex-gap-${ex.hole_id}-${ex.gap_from}`}>
        <div className="flex items-center gap-3 mb-1">
          <span className="font-mono text-slate-800 font-semibold">{ex.hole_id}</span>
          <span className="text-slate-500 text-[10.5px] uppercase">Gap</span>
          <span className="font-mono text-amber-700 font-semibold">{ex.gap_from} – {ex.gap_to} m</span>
          <span className="ml-auto text-[10.5px] font-mono text-slate-500">Δ {ex.gap_size} m</span>
          {ex.auto_safe && (
            <span className="text-[9.5px] uppercase tracking-wider px-1.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700">Auto-safe</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 text-[11px] text-slate-600">
          <span><b className="text-slate-500 uppercase text-[9.5px]">Prev:</b> {ex.prev}</span>
          <span><b className="text-slate-500 uppercase text-[9.5px]">Next:</b> {ex.next}</span>
        </div>
      </div>
    );
  }
  return <div className="text-[12px] text-slate-600">{JSON.stringify(ex)}</div>;
}


/* ═════════════════════════════════════════════════════════════════
 * Manual-fix modal (assay-gap workflow)
 * ════════════════════════════════════════════════════════════════ */
function ManualFixModal({ open, onClose, projectId, card, example, onFixed }) {
  const [reason, setReason] = useState("");
  const [holeId, setHoleId] = useState(example?.hole_id || "");
  const [from, setFrom] = useState(example?.gap_from || "");
  const [to, setTo] = useState(example?.gap_to || "");

  const markUnsampled = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/validation/gap/mark-unsampled`, {
      hole_id: holeId, from: Number(from), to: Number(to), reason,
    }).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Interval marked as unsampled · audit ${r.audit_id?.slice(0, 8)}`);
      onFixed?.();
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Mark-unsampled failed"),
  });

  const merge = useMutation({
    mutationFn: () => api.post(`/projects/${projectId}/validation/gap/merge-adjacent`, {
      hole_id: holeId, from: Number(from), to: Number(to),
    }).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Gap closed by merge · audit ${r.audit_id?.slice(0, 8)}`);
      onFixed?.();
      onClose();
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || "Merge-adjacent failed"),
  });

  const gap = useMemo(() => {
    const f = parseFloat(from), t = parseFloat(to);
    return Number.isFinite(f) && Number.isFinite(t) ? Math.max(0, t - f) : 0;
  }, [from, to]);
  const canMerge = gap > 0 && gap <= 0.001;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="manual-fix-modal">
        <DialogHeader>
          <DialogTitle>Manual review — {card?.code}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-[12.5px]">
          <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-[12px]">
            {card?.impact}
          </div>
          <div className="grid grid-cols-3 gap-2">
            <label className="col-span-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Hole ID</div>
              <Input value={holeId} onChange={(e) => setHoleId(e.target.value)} data-testid="mfix-hole" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">From (m)</div>
              <Input value={from} onChange={(e) => setFrom(e.target.value)} type="number" step="0.001" data-testid="mfix-from" />
            </label>
            <label>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">To (m)</div>
              <Input value={to} onChange={(e) => setTo(e.target.value)} type="number" step="0.001" data-testid="mfix-to" />
            </label>
            <div className="flex flex-col justify-end">
              <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Gap size</div>
              <div className="font-mono text-slate-800 h-8 flex items-center">{gap.toFixed(3)} m</div>
            </div>
          </div>
          <label className="block">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Reason (audit)</div>
            <Input value={reason} onChange={(e) => setReason(e.target.value)}
                     placeholder="e.g. Verified against original lab records — genuinely unsampled."
                     data-testid="mfix-reason" />
          </label>
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11.5px] text-slate-600">
            <b>Merge-adjacent</b> is only allowed for gaps ≤ 0.001 m (precision-rounding).
            For genuine gaps, use <b>Mark as Unsampled</b> — this inserts an explicit
            interval with a null grade so the geological record is preserved without
            fabricating assay values.
          </div>
        </div>
        <DialogFooter className="mt-3 flex-wrap gap-2">
          <Button variant="ghost" onClick={onClose} data-testid="mfix-cancel">Cancel</Button>
          <Button variant="outline"
                     disabled={!canMerge || merge.isPending}
                     onClick={() => merge.mutate()}
                     className="gap-1 text-[11.5px]"
                     data-testid="mfix-merge">
            <MagicWand size={12} /> Merge-adjacent (≤ 0.001 m)
          </Button>
          <Button disabled={!holeId || markUnsampled.isPending}
                     onClick={() => markUnsampled.mutate()}
                     className="gap-1 text-[11.5px] bg-brand-blue hover:bg-brand-blue/90 text-white"
                     data-testid="mfix-unsampled">
            Mark as Unsampled
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ═════════════════════════════════════════════════════════════════
 * Iter98h · Rollback Reason Analytics mini panel
 * ════════════════════════════════════════════════════════════════ */
function RollbackAnalyticsMini({ projectId }) {
  const q = useQuery({
    queryKey: ["autofix-rollback-analytics", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/validation/autofix-rollback-analytics`).then((r) => r.data),
    enabled:  !!projectId,
    staleTime: 20_000,
  });
  const d = q.data;
  if (!d || d.total_runs === 0) return null;
  const months = d.months || [];
  const maxTotal = Math.max(1, ...months.map((m) => m.total));
  return (
    <div className="mb-3 rounded-md border border-slate-200 bg-white p-3"
             data-testid="val-rollback-analytics">
      <div className="flex items-center gap-2 mb-2">
        <div className="text-[10.5px] font-bold uppercase tracking-widest text-slate-500 flex-1">
          Rollback trend
        </div>
        <span className="text-[10.5px] font-mono text-slate-500">
          {d.total_runs} run{d.total_runs === 1 ? "" : "s"} · {d.total_docs} record{d.total_docs === 1 ? "" : "s"} restored
        </span>
      </div>
      {/* Monthly bar chart (inline SVG — no extra dependency) */}
      <div className="flex items-end gap-1.5 h-16 mb-2" role="img"
             aria-label="Monthly rollback counts">
        {months.map((m) => (
          <div key={m.month} className="flex-1 flex flex-col items-center gap-0.5"
                 title={`${m.month}: ${m.total} rollback${m.total === 1 ? "" : "s"}`}
                 data-testid={`val-rollback-bar-${m.month}`}>
            <div className="w-full flex items-end justify-center relative"
                    style={{ height: "44px" }}>
              <div className="w-full rounded-sm bg-gradient-to-t from-rose-500 to-rose-400"
                      style={{ height: `${Math.max(2, (m.total / maxTotal) * 44)}px` }} />
            </div>
            <div className="text-[9px] font-mono text-slate-500">{m.month.slice(5)}</div>
          </div>
        ))}
      </div>
      {/* Top reasons list */}
      {(d.top_reasons || []).length > 0 && (
        <div className="pt-2 border-t border-slate-100">
          <div className="text-[9.5px] uppercase tracking-widest text-slate-500 mb-1">Top reasons</div>
          <ul className="text-[11px] space-y-0.5">
            {d.top_reasons.slice(0, 5).map((r, i) => (
              <li key={i} className="flex items-center gap-2"
                    data-testid={`val-rollback-reason-${i}`}>
                <span className="w-3 h-3 rounded-sm bg-rose-400/60 shrink-0" />
                <span className="flex-1 text-slate-700 truncate" title={r.reason}>{r.reason}</span>
                <span className="font-mono text-slate-500">×{r.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════
 * Correction-history drawer
 * ════════════════════════════════════════════════════════════════ */
function HistoryDrawer({ open, onClose, projectId }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["validation-history", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/validation/history`).then((r) => r.data),
    enabled:  !!projectId && open,
  });
  // Iter98d · Autofix runs timeline — separate query so we can list
  // every Safe Auto-Fix run with a per-run Undo button.
  const runsQ = useQuery({
    queryKey: ["autofix-runs", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/validation/autofix-runs`).then((r) => r.data),
    enabled:  !!projectId && open,
    staleTime: 5_000,
  });
  const [tab, setTab] = useState("runs");
  const [diffRunId, setDiffRunId] = useState(null);
  // Iter98e · Bulk undo — set of selected reversible run_ids.
  const [selected, setSelected] = useState(new Set());

  const rollback = useMutation({
    mutationFn: (runId) => api.post(`/projects/${projectId}/validation/safe-autofix/rollback/${runId}`).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Rollback complete · ${r.restored} restored${r.reinserted ? ` · ${r.reinserted} re-inserted` : ""}`);
      qc.invalidateQueries({ queryKey: ["autofix-runs", projectId] });
      qc.invalidateQueries({ queryKey: ["validation-v2", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Rollback failed"),
  });

  // Iter98e · Bulk rollback — accept a list of run_ids and hit the
  // batch endpoint. Runs are reverted newest-first server-side.
  const rollbackBulk = useMutation({
    mutationFn: ({ runIds, reason }) => api.post(`/projects/${projectId}/validation/safe-autofix/rollback-many`,
                                                        { run_ids: runIds, reason }).then((r) => r.data),
    onSuccess: (r) => {
      toast.success(`Bulk rollback · ${r.processed} runs · ${r.total_restored} restored${r.total_reinserted ? ` · ${r.total_reinserted} re-inserted` : ""}`);
      setSelected(new Set());
      setBulkPreview(null);
      setBulkReason("");
      qc.invalidateQueries({ queryKey: ["autofix-runs", projectId] });
      qc.invalidateQueries({ queryKey: ["validation-v2", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Bulk rollback failed"),
  });

  // Iter98f · Preview state — dialog shown before firing rollbackBulk.
  const [bulkPreview, setBulkPreview] = useState(null);
  // Iter98g · Rollback comment — required one-line reason recorded on
  // every audit-log entry for auditor-friendly provenance.
  const [bulkReason, setBulkReason]   = useState("");
  const previewBulk = useMutation({
    mutationFn: (runIds) => api.post(`/projects/${projectId}/validation/safe-autofix/rollback-many/preview`,
                                          { run_ids: runIds }).then((r) => r.data),
    onSuccess: (data, runIds) => setBulkPreview({ ...data, run_ids: runIds }),
    onError: (e) => toast.error(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Preview failed"),
  });

  const toggleSelected = (runId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(runId)) next.delete(runId); else next.add(runId);
      return next;
    });
  };

  const diffQ = useQuery({
    queryKey: ["autofix-diff", projectId, diffRunId],
    queryFn:  () => api.get(`/projects/${projectId}/validation/autofix-runs/${diffRunId}/diff`).then((r) => r.data),
    enabled:  !!projectId && !!diffRunId,
  });

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="true" data-testid="val-history-drawer"
             className="fixed inset-0 z-50 bg-slate-950/40"
             onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="absolute right-0 top-0 h-full w-full max-w-3xl bg-white shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-slate-500">Immutable audit trail</div>
            <h2 className="mt-1 font-heading font-black text-2xl tracking-tight">Correction History</h2>
            <p className="mt-1 text-[12px] text-slate-500">Every validation correction ever applied. Append-only.</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} data-testid="val-history-close" className="gap-1">
            <X size={12} /> Close
          </Button>
        </div>
        {/* Iter98d · Tabs — Auto-Fix Runs (revertable) vs full correction history */}
        <div className="px-6 pt-4 border-b border-slate-100 flex gap-1">
          <button
            type="button" data-testid="val-history-tab-runs"
            onClick={() => setTab("runs")}
            className={`text-[12px] font-semibold px-3 py-2 border-b-2 ${
              tab === "runs" ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            Auto-Fix Runs {runsQ.data?.count ? <span className="text-slate-400">({runsQ.data.count})</span> : null}
          </button>
          <button
            type="button" data-testid="val-history-tab-history"
            onClick={() => setTab("history")}
            className={`text-[12px] font-semibold px-3 py-2 border-b-2 ${
              tab === "history" ? "border-brand-blue text-brand-blue" : "border-transparent text-slate-500 hover:text-slate-800"
            }`}>
            All Corrections {q.data?.count ? <span className="text-slate-400">({q.data.count})</span> : null}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
        {tab === "runs" ? (
          runsQ.isLoading ? (
            <div className="animate-pulse space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-16 bg-slate-100 rounded" />)}
            </div>
          ) : (runsQ.data?.runs || []).length === 0 ? (
            <div className="text-center text-slate-500 py-8" data-testid="val-runs-empty">
              No Safe Auto-Fix runs yet.
            </div>
          ) : (
            <>
              <RollbackAnalyticsMini projectId={projectId} />
              {selected.size > 0 && (
                <div className="mb-3 sticky top-0 z-10 rounded-md border border-brand-blue/40 bg-brand-blue/5 px-3 py-2 flex items-center gap-2"
                       data-testid="val-runs-bulk-bar">
                  <span className="text-[12px] text-slate-700 flex-1">
                    <b>{selected.size}</b> run{selected.size === 1 ? "" : "s"} selected
                  </span>
                  <Button variant="ghost" size="sm" className="h-7 text-[11px]"
                             onClick={() => setSelected(new Set())}
                             data-testid="val-runs-bulk-clear">
                    Clear
                  </Button>
                  <Button variant="destructive" size="sm" className="h-7 text-[11px]"
                             onClick={() => previewBulk.mutate(Array.from(selected))}
                             disabled={previewBulk.isPending || rollbackBulk.isPending}
                             data-testid="val-runs-bulk-undo">
                    {previewBulk.isPending ? "Preparing…" : `Undo ${selected.size} run${selected.size === 1 ? "" : "s"}`}
                  </Button>
                </div>
              )}
              <ol className="space-y-2">
              {runsQ.data.runs.map((r) => {
                const isOpen = diffRunId === r.run_id;
                const isSelected = r.run_id && selected.has(r.run_id);
                return (
                  <li key={r.run_id || r.ts}
                        className={`rounded-md border p-3 text-[12.5px] ${isSelected ? "border-brand-blue bg-brand-blue/5" : "border-slate-200 bg-white"}`}
                        data-testid={`val-runs-row-${r.run_id?.slice(0, 8) || "row"}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <input
                        type="checkbox"
                        className="w-3.5 h-3.5 accent-brand-blue"
                        checked={!!isSelected}
                        disabled={!r.reversible}
                        onChange={() => r.run_id && toggleSelected(r.run_id)}
                        data-testid={`val-runs-select-${r.run_id?.slice(0, 8)}`}
                        title={r.reversible ? "Select for bulk undo" : "Not reversible"}
                      />
                      <span className="font-mono text-[10.5px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
                        {r.run_id?.slice(0, 8) || "—"}
                      </span>
                      <span className={`text-[10px] uppercase tracking-widest px-1.5 rounded border ${
                        r.rolled_back
                          ? "border-slate-300 bg-slate-100 text-slate-500 line-through"
                          : "border-emerald-300 bg-emerald-50 text-emerald-700"
                      }`}>
                        {r.rolled_back ? "reverted" : "applied"}
                      </span>
                      <span className="text-slate-700 font-medium">
                        {r.applied} record{r.applied === 1 ? "" : "s"} corrected
                        {r.unfixed ? ` · ${r.unfixed} unfixed` : ""}
                      </span>
                      <span className="ml-auto text-slate-500 text-[11px] font-mono">
                        {String(r.ts || "").slice(0, 19).replace("T", " ")}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 flex-wrap">
                      {r.user_email && <span className="text-slate-500 text-[11.5px] font-mono">by {r.user_email}</span>}
                      {r.rolled_back && r.rolled_back_at && (
                        <span className="text-slate-500 text-[11.5px]">
                          · reverted {String(r.rolled_back_at).slice(0, 19).replace("T", " ")}
                          {r.rolled_back_by && <> by <span className="font-mono">{r.rolled_back_by}</span></>}
                        </span>
                      )}
                      <div className="ml-auto flex items-center gap-1.5">
                        <Button variant="outline" size="sm" className="h-7 text-[11px]"
                                    onClick={() => setDiffRunId(isOpen ? null : r.run_id)}
                                    disabled={!r.run_id}
                                    data-testid={`val-runs-diff-${r.run_id?.slice(0, 8)}`}>
                          {isOpen ? "Hide diff" : "Preview diff"}
                        </Button>
                        <Button size="sm" variant="destructive"
                                    className="h-7 text-[11px]"
                                    disabled={!r.reversible || rollback.isPending}
                                    onClick={() => rollback.mutate(r.run_id)}
                                    data-testid={`val-runs-undo-${r.run_id?.slice(0, 8)}`}>
                          {rollback.isPending ? "…" : (r.rolled_back ? "Reverted" : "Undo run")}
                        </Button>
                      </div>
                    </div>

                    {isOpen && r.run_id && (
                      <div className="mt-2 rounded border border-slate-200 bg-slate-50 p-2 text-[11.5px]"
                            data-testid={`val-runs-diff-panel-${r.run_id?.slice(0, 8)}`}>
                        {diffQ.isLoading ? (
                          <div className="text-slate-500">Loading diff…</div>
                        ) : (diffQ.data?.entries || []).length === 0 ? (
                          <div className="text-slate-500 italic">No per-doc backups available for this run.</div>
                        ) : (
                          <>
                            <div className="text-slate-500 mb-1">
                              Showing {diffQ.data.entries.length} of {diffQ.data.total} affected records
                              {diffQ.data.truncated ? " (truncated)" : ""}.
                            </div>
                            <ul className="space-y-1">
                              {diffQ.data.entries.slice(0, 10).map((d, i) => (
                                <li key={i} className="rounded border border-slate-200 bg-white p-1.5">
                                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500">
                                    <span>{d.kind}</span>
                                    <span className="text-slate-300">·</span>
                                    <span className="text-rose-600">{d.op}</span>
                                  </div>
                                  <pre className="mt-0.5 whitespace-pre-wrap break-all font-mono text-[10.5px] text-slate-700">
                                    - {JSON.stringify(d.before)}
                                    {d.op === "update" && d.after ? "\n+ " + JSON.stringify(d.after) : ""}
                                  </pre>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
            </>
          )
        ) : (
          q.isLoading ? (
            <div className="animate-pulse space-y-2">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 bg-slate-100 rounded" />)}
            </div>
          ) : (q.data?.history || []).length === 0 ? (
            <div className="text-center text-slate-500 py-8" data-testid="val-history-empty">
              No corrections applied for this project yet.
            </div>
          ) : (
            <ol className="space-y-2">
              {q.data.history.map((h) => (
                <li key={h.id} className="rounded-md border border-slate-200 bg-white p-3 text-[12.5px]"
                        data-testid={`val-history-row-${h.id}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-[10.5px] px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-slate-700">
                      {h.code}
                    </span>
                    <span className={`text-[10px] uppercase tracking-widest px-1.5 rounded border ${h.fix_type === "auto"
                      ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                      : "border-amber-300 bg-amber-50 text-amber-700"}`}>
                      {h.fix_type}
                    </span>
                    <span className="text-slate-700 font-medium">{h.action}</span>
                    <span className="ml-auto text-slate-500 text-[11px] font-mono">{String(h.timestamp || "").slice(0, 19).replace("T", " ")}</span>
                  </div>
                  <div className="mt-1 text-slate-600 text-[12px]">
                    <b>{h.affected}</b> record{h.affected === 1 ? "" : "s"} affected
                    {h.user_email && <> · by <span className="font-mono">{h.user_email}</span></>}
                  </div>
                  {h.reason && <div className="mt-1 text-slate-500 text-[11.5px] italic">{h.reason}</div>}
                </li>
              ))}
            </ol>
          )
        )}
        </div>
      </div>
      {/* Iter98f · Bulk rollback preview dialog */}
      {bulkPreview && (
        <div role="dialog" aria-modal="true"
               data-testid="val-runs-bulk-preview"
               className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/60 p-4"
               onClick={(e) => e.target === e.currentTarget && setBulkPreview(null)}>
          <div className="w-full max-w-lg rounded-lg bg-white shadow-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-200">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">Rollback preview</div>
              <h3 className="mt-0.5 font-heading font-bold text-lg tracking-tight text-slate-900">
                Undo {bulkPreview.runs?.length || 0} auto-fix run{(bulkPreview.runs?.length || 0) === 1 ? "" : "s"}
              </h3>
              <p className="mt-0.5 text-[12px] text-slate-500">
                Review the impact below before confirming. Every restored record is written back with its original data field.
              </p>
            </div>
            <div className="p-5 space-y-3 max-h-[60vh] overflow-y-auto">
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-md border border-slate-200 bg-slate-50 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500">Total docs</div>
                  <div className="text-xl font-bold text-slate-900" data-testid="val-runs-preview-total">
                    {bulkPreview.total_docs}
                  </div>
                </div>
                <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-emerald-700">Updates restored</div>
                  <div className="text-xl font-bold text-emerald-800">{bulkPreview.total_updates}</div>
                </div>
                <div className="rounded-md border border-blue-200 bg-blue-50 p-2 text-center">
                  <div className="text-[10px] uppercase tracking-widest text-blue-700">Deletes re-inserted</div>
                  <div className="text-xl font-bold text-blue-800">{bulkPreview.total_deletes}</div>
                </div>
              </div>
              {Object.keys(bulkPreview.by_kind || {}).length > 0 && (
                <div className="rounded-md border border-slate-200 p-2">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1">Breakdown by kind</div>
                  <ul className="text-[11.5px] text-slate-700 space-y-0.5">
                    {Object.entries(bulkPreview.by_kind || {}).map(([k, n]) => (
                      <li key={k} className="flex items-center gap-2">
                        <span className="font-mono font-semibold w-16">{k}</span>
                        <span>{n} record{n === 1 ? "" : "s"}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {bulkPreview.already_reverted > 0 && (
                <div className="text-[11.5px] text-amber-700">
                  <b>{bulkPreview.already_reverted}</b> of the selected runs are already rolled back and will be skipped.
                </div>
              )}
              {/* Iter98g · Required reason for audit-log provenance. */}
              <div>
                <label className="text-[10px] uppercase tracking-widest text-slate-500 mb-1 block">
                  Reason for rollback <span className="text-rose-600">*</span>
                </label>
                <Input
                  value={bulkReason}
                  onChange={(e) => setBulkReason(e.target.value)}
                  placeholder="e.g. incorrect hole-id normalisation, will re-run with different rule"
                  className="h-8 text-[12px]"
                  autoFocus
                  data-testid="val-runs-preview-reason"
                />
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2 bg-slate-50">
              <button
                type="button"
                onClick={() => { setBulkPreview(null); setBulkReason(""); }}
                className="text-[12px] text-slate-500 hover:text-slate-800"
                data-testid="val-runs-preview-cancel">
                Cancel
              </button>
              <Button variant="destructive" size="sm"
                        className="h-8 text-[12px]"
                        onClick={() => rollbackBulk.mutate({
                          runIds: bulkPreview.run_ids || Array.from(selected),
                          reason: bulkReason.trim(),
                        })}
                        disabled={rollbackBulk.isPending || bulkPreview.total_docs === 0 || bulkReason.trim().length < 3}
                        data-testid="val-runs-preview-confirm">
                {rollbackBulk.isPending ? "Reverting…" : `Confirm undo · restore ${bulkPreview.total_docs} record${bulkPreview.total_docs === 1 ? "" : "s"}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═════════════════════════════════════════════════════════════════
 * Hole-detail modal (lightweight — full detail is on the wizard step)
 * ════════════════════════════════════════════════════════════════ */
function HoleModal({ open, onClose, projectId, holeId }) {
  const q = useQuery({
    queryKey: ["hole-detail", projectId, holeId],
    queryFn:  () => api.get(`/projects/${projectId}/drillholes/hole/${holeId}`).then((r) => r.data),
    enabled:  !!projectId && !!holeId && open,
  });
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="hole-detail-modal">
        <DialogHeader><DialogTitle>Hole {holeId}</DialogTitle></DialogHeader>
        {q.isLoading ? (
          <div className="animate-pulse space-y-2">
            {Array.from({length: 6}).map((_, i) => <div key={i} className="h-6 bg-slate-100 rounded" />)}
          </div>
        ) : q.error ? (
          <div className="text-[12px] text-slate-500 py-2">
            Detailed hole view is not available for this project. Open the Data Upload step to review CSV rows.
          </div>
        ) : (
          <pre className="text-[10.5px] font-mono bg-slate-50 p-3 rounded overflow-x-auto">
            {JSON.stringify(q.data, null, 2)}
          </pre>
        )}
      </DialogContent>
    </Dialog>
  );
}


/* ── Fallback states ─────────────────────────────────────────── */
function LoadingShell() {
  return (
    <div className="space-y-3 animate-pulse" data-testid="validation-loading">
      <div className="h-14 bg-slate-100 rounded" />
      {Array.from({ length: 3 }).map((_, i) => <div key={i} className="h-40 bg-slate-100 rounded" />)}
    </div>
  );
}
function ErrorShell({ error }) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-6 text-[13px] text-rose-800"
            data-testid="validation-error">
      <Warning size={14} className="inline mr-1" />
      Failed to load validation data. {error?.response?.data?.detail?.message || error?.message}
    </div>
  );
}
